import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { media, style } from 'typestyle';
import * as _ from 'underscore';

import { MetricDetailView, MetricRefreshTime, MetricTitle } from '@tableau/metrics-shared-widgets-dom';
import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { showActionableToast } from '../../../common/react/components/ActionableMessage';
import { FavoriteToggle } from '../../../common/react/components/FavoriteToggle/FavoriteToggle';
import { getMergedMetricInfo, metricDetailProps  } from '../../../metrics/metricCardPropsFromDataPoints';
import { L10n } from '../../../ts/L10n/L10n';
import { IMetricInfo } from '../../../ts/models/IMetricInfo';
import { ISpaceTypeInfo } from '../../../ts/models/resourceInfoParts';
import { MetricDataPoints } from '../../../ts/resources/MetricDataPoints';
import { Metrics } from '../../../ts/resources/Metrics';
import { Projects } from '../../../ts/resources/Projects';
import { ExploreContentApi_ContentType } from '../../../ts/ServerApi/types/IContent';
import { IMetricDataList } from '../../../ts/ServerApi/types/IMetricDataList';
import { BrowserTitleService } from '../../../ts/services/BrowserTitleService';
import { ToastType } from '../../../ts/services/ToasterService';
import { setSpaceInfoItem } from '../../actions/spaces/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '../../actions/spaces/FavoritesActions';
import { resumeMetric } from '../../actions/spaces/UpdateMetricsActions';
import { appStore } from '../../AppStore';
import { addContentTypeToProjectHierarchy, populateContentBreadcrumbs } from '../../content/breadcrumbs/BreadcrumbHelpers';
import { ServiceLocator } from '../../ServiceLocator';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '../../state/SpaceInfo';
import { MapDispatchToProps } from '../../utils/ConnectUtils';
import { currentUserIsOwnerOrAdmin } from '../../utils/MixedContentHelpers';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { RouterError } from '../RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedMetricDetailShareButton } from './MetricDetailShareButton';

export interface MetricDetailSpaceOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly id: string;
  readonly renderMetricDetailSpace: (state: MetricDetailSpaceState, addFavorite: () => void, removeFavorite: () => void, urls: SpacesSiteUrls) => JSX.Element;
  readonly userCanResumeMetric: (metricInfo: IMetricInfo) => boolean;
  readonly showSuspendedMetricWarning: (metricInfo: IMetricInfo, resumeMetric: (metricInfo: IMetricInfo) => void) => void;
}

export interface MetricDetailSpaceAngularProps {
  readonly metricsResource: Metrics;
  readonly metricsDataResource: MetricDataPoints;
  readonly browserTitleService: BrowserTitleService;
  readonly projects: Projects;
}

export interface MetricDetailSpaceDispatchProps {
  readonly addContentFavorite: (contentId: string, contentType: ExploreContentApi_ContentType) => any;
  readonly removeContentFavorite: (contentId: string, contentIdForFavoriting: string, contentType: ExploreContentApi_ContentType, contentName: string) => any;
  readonly setSpaceInfoItem: (spaceInfo: SpaceInfo) => void;
  readonly resumeMetric: (metricInfo: IMetricInfo) => void;
}

export interface MetricDetailSpaceState {
  readonly metricInfo: IMetricInfo;
  readonly hasError: boolean;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
}

const InitialState: MetricDetailSpaceState = {
  metricInfo: null,
  hasError: false,
  initialRender: true,
  renderChildren: false,
};

export type MetricDetailSpacePropsWithoutAngular = MetricDetailSpaceOwnProps & MetricDetailSpaceDispatchProps;
export type MetricDetailSpaceProps = MetricDetailSpacePropsWithoutAngular & MetricDetailSpaceAngularProps;

export class MetricDetailSpace extends React.PureComponent<MetricDetailSpaceProps, MetricDetailSpaceState> {
  private unmounted: boolean = false;
  private spaceInfo: SpaceInfo = undefined;

  public constructor(props: MetricDetailSpaceProps) {
    super(props);
    this.state = InitialState;
    this.spaceInfo = { spaceType: 'metric', subspaceName: undefined, item: SpaceItemForNonEntitySpace /* populated after fetching data */ };
  }

  public componentDidMount() {
    this.unmounted = false;
    this.fetchMetric();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate(prevProps: MetricDetailSpaceProps) {
    if (this.changedMetric(prevProps)) {
      this.updateState(InitialState /* want the initial state for the metric */);
      this.fetchMetric();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender && this.state.metricInfo) {
      this.updateState({ initialRender: false, renderChildren: true });

      // Update recently viewed and the metric view count
      this.props.metricsResource.markRecentlyViewed(this.state.metricInfo.id);
      this.props.metricsResource.markViewed(this.state.metricInfo.id);

        // Show warning toast if metric is suspended
      if (this.state.metricInfo.suspended && this.props.userCanResumeMetric(this.state.metricInfo)) {
        this.props.showSuspendedMetricWarning(this.state.metricInfo, this.props.resumeMetric);
      }
    }
  }

  private updateState<K extends keyof MetricDetailSpaceState>(newState: Pick<MetricDetailSpaceState, K>): void {
    if (!this.unmounted) {
      this.setState(newState, this.updateTitle);
    }
  }

  private updateTitle() {
    if (this.state.metricInfo) {
      this.props.browserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.state.metricInfo.name });
    } else {
      this.props.browserTitleService.setTitleToServerName();
    }
  }

  private changedMetric(prevProps: MetricDetailSpaceProps) {
    return !_.isUndefined(prevProps.id)
      && !_.isUndefined(this.props.id)
      && prevProps.id !== this.props.id;
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.metricInfo);
  }

  private fetchMetric = async () => {
    try {
      const metricDataList: IMetricDataList = await this.props.metricsDataResource.getMetricDataPoints([this.props.id]);
      const metricInfo = await this.props.metricsResource.getById(this.props.id);

      if (!!metricInfo && !!metricDataList) {
        const metric = getMergedMetricInfo(metricInfo, metricDataList);

        this.spaceInfo = { item: { ...metric, contentType: 'metric', id: this.props.id }, spaceType: 'metric', subspaceName: undefined };
        this.props.setSpaceInfoItem(this.spaceInfo);
        this.updateState({ metricInfo: metric });

        const projectHierarchy = await this.props.projects.getProjectHierarchy(metric.project.id);
        const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
        populateContentBreadcrumbs([ ...projectHierarchyWithType, metric ], this.props.urls);

      } else {
        this.updateState({ hasError: true, renderChildren: false });
      }
    } catch (e) {
      this.updateState({ hasError: true, renderChildren: false });
    }
  }

  private addFavorite = () => {
    if (this.state.metricInfo) {
      this.props.addContentFavorite(this.state.metricInfo.id, 'metric');
    }
  }

  private removeFavorite = () => {
    if (this.state.metricInfo) {
      this.props.removeContentFavorite(this.state.metricInfo.id, this.state.metricInfo.id, 'metric', this.state.metricInfo.name);
    }
  }

  public render() {
    let element: JSX.Element;
    if (this.state.renderChildren) {
      element = this.props.renderMetricDetailSpace(this.state, this.addFavorite, this.removeFavorite, this.props.urls);
    } else if (this.state.hasError) {
      element = <RouterError errorType={'metricDetail'} urls={this.props.urls} />;
    } else {
      element = null;
    }

    return (
      <ConnectedSpaceInfoProvider spaceInfo={this.spaceInfo}>
        {element}
      </ConnectedSpaceInfoProvider>);
  }
}

export function renderMetricDetailSpace(state: MetricDetailSpaceState, addFavorite: () => void, removeFavorite: () => void, urls: SpacesSiteUrls) {
  const p = metricDetailProps(state.metricInfo, urls);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      <div className={containerStyle}>
        {renderTopRow(state, addFavorite, removeFavorite, urls)}
        <MetricDetailView
          {...p}
          style={{
            lineHeight: 'normal',
          }}
        />
      </div>
    </div>
  );
}

export function userCanResumeMetric(metricInfo: IMetricInfo) {
  return currentUserIsOwnerOrAdmin(appStore.getState(), metricInfo);
}

export function showSuspendedMetricWarning(metricInfo: IMetricInfo, resumeMetric: (metricInfo: IMetricInfo) => void) {
  showActionableToast({
    message: Messages.metricSuspended({ SUSPEND_DATE: L10n.localizedDate(metricInfo.dataRefreshedTimestamp) }),
    testId: `suspended-metric-warning`,
    actionMessage: Messages.metricResumeButtonLabel(),
    action: () => resumeMetric(metricInfo)
  }, ToastType.Warning);
}

function renderTopRow(state: MetricDetailSpaceState, addFavorite: () => void, removeFavorite: () => void, urls: SpacesSiteUrls) {
  const p = metricDetailProps(state.metricInfo, urls);

  return (
    <div style={topRowContainerStyle}>
      <div style={titleAndActionsContainerStyle}>
        <MetricRefreshTime lastRefreshedTimestamp={p.lastUpdatedTimestamp} />
        <div style={titleRowLayoutStyle}>
          <div style={titleLayoutStyle}>
            <MetricTitle metricName={p.metricName} />
          </div>
          <div style={favoriteLayoutStyle}>
            <FavoriteToggle isFavorite={state.metricInfo.favorite} onAddFavorite={addFavorite} onRemoveFavorite={removeFavorite} canHaveKeyboardFocus />
          </div>
          <div style={actionsLayoutStyle}>
            <ActionsMenu targetItems={[ state.metricInfo ]} useIcon />
          </div>
        </div>
      </div>
      <div className={shareLayoutStyle}>
        <ConnectedMetricDetailShareButton metricInfo={state.metricInfo} />
      </div>
    </div>
  );
}

export namespace MetricDetailSpaceAngularProvider {
  function getAngularServicesWithServiceLocator() {
    const Metrics = ServiceLocator.get<Metrics>('Metrics');
    const MetricsData = ServiceLocator.get<MetricDataPoints>('MetricDataPoints');
    const BrowserTitleService = ServiceLocator.get<BrowserTitleService>('BrowserTitleService');

    return {
      metricsResource: Metrics,
      metricsDataResource: MetricsData,
      browserTitleService: BrowserTitleService,
      projects: ServiceLocator.get<Projects>('Projects'),
    };
  }

  function selectPropsFromAngularServices(angularServices: MetricDetailSpaceAngularProps) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<MetricDetailSpacePropsWithoutAngular, MetricDetailSpaceAngularProps, MetricDetailSpaceAngularProps>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const MetricDetailSpaceAng: React.ComponentType<MetricDetailSpacePropsWithoutAngular> = MetricDetailSpaceAngularProvider.HOC(MetricDetailSpace);

const mapDispatchToProps: MapDispatchToProps<MetricDetailSpaceDispatchProps> = (dispatch) => bindActionCreators({
  addContentFavorite: addContentFavorite,
  removeContentFavorite: removeContentFavorite,
  setSpaceInfoItem: setSpaceInfoItem,
  resumeMetric: resumeMetric,
}, dispatch);

export const ConnectedMetricDetailSpace: React.ComponentClass<MetricDetailSpaceOwnProps>
  = connect(undefined, mapDispatchToProps)(MetricDetailSpaceAng);

const containerStyle: string = style(
  { marginLeft: '24px', marginRight: '24px', marginTop: '24px' },
  media({ minWidth: 1025 }, { marginLeft: '56px' }),
);
const topRowContainerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
};
const titleAndActionsContainerStyle: React.CSSProperties = {
  display: 'flex',
  minWidth: 0,
  flexDirection: 'column',
  overflow: 'hidden',
};
const titleRowLayoutStyle: React.CSSProperties = {
  display: 'flex',
  minWidth: 0,
  marginTop: '12px',
  alignItems: 'center',
};
const titleLayoutStyle: React.CSSProperties = {
  minWidth: 0,
  marginRight: '24px',
  overflow: 'hidden',
};
const favoriteLayoutStyle: React.CSSProperties = {
  marginRight: '12px',
  flexShrink: 0,
};
const actionsLayoutStyle: React.CSSProperties = {
  flexShrink: 0,
};
const shareLayoutStyle: string = style(
  { alignSelf: 'flex-start' },
  media({ maxWidth: 375 }, { display: 'none' })
);
